import React, { useRef, useState } from 'react';
import {
  ClickAwayListener,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Popper,
  Select
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Button } from 'system/atoms';
import { rangeFilterSx } from 'common/table/styles/rangeFilterSx';
import { MinMax, TableV3FilterItem } from 'common/table/types/TableV3';
import {
  tableFilterItemsToMinMax,
  minMaxToTableFilterItems
} from 'common/table/utils/minMaxTableFilterItems';

export type RangeFilterFields<Draft> = {
  draft: Draft;
  setDraft: React.Dispatch<React.SetStateAction<Draft>>;
  onKeyDown: (event: React.KeyboardEvent) => void;
};

type TableV3RangeFilterPopoverProps<
  Comparator extends string,
  Draft extends { comparator: Comparator }
> = {
  selectedItems: TableV3FilterItem[];
  isSorted?: boolean;
  onFilterApply: (items: TableV3FilterItem[]) => void;
  comparators: { value: Comparator; label: string }[];
  tableFilterItemsToDraft: (items: TableV3FilterItem[]) => Draft;
  draftToMinMax: (draft: Draft) => MinMax | null;
  renderFields: (fields: RangeFilterFields<Draft>) => React.ReactNode;
};

export const TableV3RangeFilterPopover = <
  Comparator extends string,
  Draft extends { comparator: Comparator }
>({
  selectedItems,
  isSorted = false,
  onFilterApply,
  comparators,
  tableFilterItemsToDraft,
  draftToMinMax,
  renderFields
}: TableV3RangeFilterPopoverProps<Comparator, Draft>): React.ReactElement => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(() =>
    tableFilterItemsToDraft(selectedItems)
  );

  const minMax = draftToMinMax(draft);
  const committed = tableFilterItemsToMinMax(selectedItems);
  const isChanged =
    minMax?.min !== committed.min || minMax?.max !== committed.max;

  const apply = () => {
    setOpen(false);
    if (minMax) onFilterApply(minMaxToTableFilterItems(minMax));
  };

  const close = () => (minMax && isChanged ? apply() : setOpen(false));

  const onToggle = () => {
    if (open) return close();
    setDraft(tableFilterItemsToDraft(selectedItems));
    setOpen(true);
  };

  const onClear = () => {
    setOpen(false);
    onFilterApply([]);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter' || !minMax) return;
    event.preventDefault();
    apply();
  };

  return (
    <>
      <IconButton
        ref={anchorRef}
        onClick={onToggle}
        sx={rangeFilterSx.iconButton(isSorted, selectedItems.length > 0)}
      >
        <FilterListIcon fontSize="small" />
      </IconButton>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={rangeFilterSx.popper}
        modifiers={[{ name: 'flip', enabled: false }]}
      >
        <ClickAwayListener
          mouseEvent="onMouseDown"
          onClickAway={(event) => {
            if (!anchorRef.current?.contains(event.target as Node)) close();
          }}
        >
          <Paper ref={paperRef} sx={rangeFilterSx.paper}>
            <Select
              size="small"
              fullWidth
              value={draft.comparator}
              sx={rangeFilterSx.select}
              SelectDisplayProps={{ 'aria-label': 'Comparator' }}
              MenuProps={{
                TransitionProps: {
                  onExited: () =>
                    paperRef.current
                      ?.querySelector<HTMLInputElement>(
                        'input:not([aria-hidden])'
                      )
                      ?.focus()
                }
              }}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  comparator: event.target.value as Comparator
                }))
              }
            >
              {comparators.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
            {renderFields({ draft, setDraft, onKeyDown })}
            <Button
              variant="primary"
              fullWidth
              disabled={!minMax}
              onClick={apply}
              sx={rangeFilterSx.apply}
            >
              Apply
            </Button>
            <Link
              component="button"
              type="button"
              underline="none"
              onClick={onClear}
              sx={rangeFilterSx.clear}
            >
              Clear
            </Link>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
